import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import './global.css';
import App from './App.tsx';
import { getBoot } from './utils/boot';
import {
  preloadBackgroundImage,
  preloadFontAwesomeFonts,
  preloadEmbeddedViewFont,
  prewarmPspChunks,
  prewarmPspFromCheckoutData,
} from './utils/preloadResources';
import { kickoffCheckoutDataPrefetch, peekPrefetch } from './api/checkoutDataPrefetch';

/**
 * Perf: pre-flight `/checkout/v3` in parallel with React mount + boot
 * decode. `useCheckoutData` is gated on `isApiInitilized` (set inside
 * `<CheckoutContent>`'s first useEffect), so without this the GET
 * cannot start until the bundle has parsed AND the React tree has
 * hydrated — pure serial cost on cold starts. The prefetch is
 * byte-identical (URL, headers, method, credentials) to what
 * `apiClient.get` would issue; on any failure the queryFn falls back
 * to the existing path, preserving the legacy contract. See
 * `checkoutDataPrefetch.ts` for the zero-regression details.
 *
 * Fired FIRST in module-eval (before `getBoot()` or `prewarmPspChunks()`)
 * because the network RTT is the longest pole on cold starts — every
 * microsecond shaved off the kickoff time directly cuts t3.
 */
kickoffCheckoutDataPrefetch();

/**
 * Perf: piggyback on the prefetch (non-consuming) to start
 * `loadStripe()` and (idle-deferred) `AdyenCheckout()` as soon as the
 * `/checkout/v3` response lands, instead of waiting for the React tree
 * to hydrate and `<CheckoutContent>`'s effects to mount. Replaces the
 * previous boot-driven path now that `boot.pks` / `boot.countryCode2` /
 * `boot.currencyCode` are no longer carried on the boot JWT.
 *
 * Registered RIGHT AFTER kickoff so this `.then` is the first handler
 * on the prefetch promise — when the response lands it runs before the
 * (much later) queryFn consumer.
 */
peekPrefetch()
  ?.then(prewarmPspFromCheckoutData)
  .catch(() => {});

/**
 * Perf: kick off the JWT decode + pako inflate so it overlaps the React
 * bundle's module evaluation. The promise's `.then` runs whenever
 * decode finishes — usually before `createRoot` is called on cold
 * loads. Used here for the boot-dependent asset preloads (background
 * image, template-specific font); PSP keys arrive on the `/checkout/v3`
 * prefetch above.
 */
getBoot()
  .then((boot) => {
    preloadBackgroundImage(boot);
    preloadEmbeddedViewFont(boot);
  })
  .catch(() => {});

preloadFontAwesomeFonts();

prewarmPspChunks();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: Infinity,
      retry: 1,
    },
  },
});

// NOTE: intentionally NOT wrapped in <StrictMode>. In dev, StrictMode
// double-invokes effects (setup → cleanup → setup), which made the Adyen
// Google Pay express button initialize twice and visibly blink (mount → unmount
// → remount) once it was ready — a local-dev-only artifact (production React
// ignores StrictMode's double-invoke, so staging never showed it). Removing the
// wrapper here makes local match production. The published SDK doesn't use this
// entry, so this only affects the standalone app / dev playground.
createRoot(document.getElementById('root')!).render(
  <QueryClientProvider client={queryClient}>
    <App />
  </QueryClientProvider>
);
